import { Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Flex, Box, VisuallyHidden } from '@chakra-ui/react'
import { PageSkeleton } from '@/components/common/LoadingSkeleton'
import { Layout } from '@/components/layout/Layout'
import { LoginPage } from '@/pages/auth'
import { DashboardPage } from '@/pages/dashboard'
import { AdminUsersPage } from '@/pages/adminUsers'
import { UsersPage } from '@/pages/users'
import { MerchantsPage } from '@/pages/merchants'
import { OrdersPage } from '@/pages/orders'
import { SystemConfigPage } from '@/pages/systemConfig'
import { MonitoringPage } from '@/pages/monitoring'
import { ContractRegistryPage } from '@/pages/contractManagement'
import { PaymentManagementPage } from '@/pages/paymentManagement'
import { RescuePage } from '@/pages/rescue'
import { AuditLogsPage } from '@/pages/auditLogs'
import { SupportPage } from '@/pages/support'
import { AnnouncementsPage } from '@/pages/announcements'
import { DocumentationContentPage } from '@/pages/documentationContent'
import { AccountPage } from '@/pages/account'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { MainnetOnlyNotice } from '@/components/common/MainnetOnlyNotice'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100vh" px={6}>
        <Box w="full" maxW="640px" role="status" aria-busy="true">
          <PageSkeleton minHeight="260px" cardCount={2} />
          <VisuallyHidden>Loading</VisuallyHidden>
        </Box>
      </Flex>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100vh" px={6}>
        <Box w="full" maxW="640px" role="status" aria-busy="true">
          <PageSkeleton minHeight="260px" cardCount={2} />
          <VisuallyHidden>Loading</VisuallyHidden>
        </Box>
      </Flex>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

function MainnetOnlyRoute({ children }: { children: React.ReactNode }) {
  const { currentEnv } = useNetworkEnvironment()
  if (currentEnv === 'testnet') {
    return <MainnetOnlyNotice />
  }
  return <>{children}</>
}

export function Router() {
  return (
    <Suspense
      fallback={
        <Flex justify="center" align="center" h="100vh" px={6}>
          <Box w="full" maxW="640px" role="status" aria-busy="true">
            <PageSkeleton minHeight="260px" cardCount={2} />
            <VisuallyHidden>Loading</VisuallyHidden>
          </Box>
        </Flex>
      }
    >
      {/* @ts-expect-error react-router future flags are runtime-only in v6 typings */}
      <Routes future={{ v7_relativeSplatPath: true }}>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route
            path="dashboard"
            element={
              <MainnetOnlyRoute>
                <DashboardPage />
              </MainnetOnlyRoute>
            }
          />
          <Route
            path="admin-users"
            element={
              <MainnetOnlyRoute>
                <AdminUsersPage />
              </MainnetOnlyRoute>
            }
          />
          <Route
            path="users"
            element={
              <MainnetOnlyRoute>
                <UsersPage />
              </MainnetOnlyRoute>
            }
          />
          <Route path="merchants" element={<MerchantsPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route
            path="system"
            element={<SystemConfigPage />}
          />
          <Route
            path="monitoring"
            element={<MonitoringPage />}
          />
          <Route path="rescue" element={<RescuePage />} />
          <Route path="contract-management" element={<ContractRegistryPage />} />
          <Route
            path="payment-management"
            element={<PaymentManagementPage />}
          />
          <Route
            path="audit"
            element={<AuditLogsPage />}
          />
          <Route
            path="support"
            element={
              <MainnetOnlyRoute>
                <SupportPage />
              </MainnetOnlyRoute>
            }
          />
          <Route
            path="announcements"
            element={
              <MainnetOnlyRoute>
                <AnnouncementsPage />
              </MainnetOnlyRoute>
            }
          />
          <Route
            path="documentation-content"
            element={<DocumentationContentPage />}
          />
          <Route path="account" element={<AccountPage />} />
          <Route
            path="account/security"
            element={<Navigate to="/account?tab=mfa" replace />}
          />
        </Route>

        {/* Catch all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  )
}
